import { Archive, ArchiveRestore, ImagePlus, Save, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import type { Limits, Persona, WidgetTheme } from "@navibiz/chat-core";
import { api } from "../../lib/api";
import { PRODUCT_LABEL } from "../../lib/format";
import { keys, useAction, useBilling, useMe } from "../../lib/queries";
import { PACKAGES } from "../overview/NewProjectDialog";
import type { Project } from "../../lib/types";
import { Button } from "../../ui/Button";
import { Card } from "../../ui/Card";
import { useToast } from "../../ui/Feedback";
import { ChipsInput, Field, FormGrid, Input, Select, Switch, Textarea } from "../../ui/Form";
import { Segmented } from "../../ui/Misc";
import { KeysPanel } from "./KeysPanel";
import { useProjectContext } from "./ProjectLayout";
import { WidgetPreview } from "./WidgetPreview";
import styles from "./SettingsTab.module.css";

export function SettingsTab() {
  const { project } = useProjectContext();
  const me = useMe().data!;
  // A client account shapes its own bot's persona and look; limits, project data, and keys stay with Navibiz.
  return (
    <div className={styles.stack}>
      <PersonaSection project={project} />
      {me.role !== "client" && <LimitsSection project={project} />}
      {me.role === "admin" && <ProjectSection project={project} />}
      {me.role === "admin" && <KeysPanel project={project} />}
      {me.role === "admin" && <ArchiveSection project={project} />}
    </div>
  );
}

function useSaveProject(projectId: string) {
  return useAction((patch: Record<string, unknown>) => api.patch(`/projects/${projectId}`, patch), [keys.project(projectId), ["overview"]]);
}

function PersonaSection({ project }: { project: Project }) {
  const toast = useToast();
  const [persona, setPersona] = useState<Persona>(project.persona);
  const [theme, setTheme] = useState<WidgetTheme>(project.theme);
  const [locale, setLocale] = useState<"id" | "en">(project.defaultLocale);
  const save = useSaveProject(project.id);
  useEffect(() => {
    setPersona(project.persona);
    setTheme(project.theme);
  }, [project.persona, project.theme]);

  // The avatar file is saved by its own upload, so it never makes the form dirty.
  const dirty = JSON.stringify(persona) !== JSON.stringify(project.persona) || JSON.stringify({ ...theme, avatarUrl: "" }) !== JSON.stringify({ ...project.theme, avatarUrl: "" });
  const p = (patch: Partial<Persona>) => setPersona({ ...persona, ...patch });
  const t = (patch: Partial<WidgetTheme>) => setTheme({ ...theme, ...patch });
  const englishMissing = theme.languageSwitch && (!persona.greeting.en.trim() || !theme.launcherLabel.en.trim());

  return (
    <Card
      title="Persona & tampilan widget"
      description="Berlaku seketika setelah disimpan — tanpa menerbitkan ulang pengetahuan."
      actions={
        <>
          <Segmented label="Bahasa pratinjau" value={locale} onChange={setLocale} options={[{ value: "id", label: "ID" }, { value: "en", label: "EN" }]} />
          <Button
            variant="primary"
            icon={<Save aria-hidden />}
            disabled={!dirty}
            loading={save.isPending}
            onClick={async () => {
              await save.mutateAsync({ persona, theme });
              toast("Persona dan tampilan disimpan.");
            }}
          >
            Simpan
          </Button>
        </>
      }
    >
      <div className={styles.personaGrid}>
        <div className={styles.form}>
          <h3 className={styles.sectionTitle}>Persona</h3>
          <FormGrid>
            <Field label="Nama asisten">{(id) => <Input id={id} value={persona.name} maxLength={40} onChange={(e) => p({ name: e.target.value })} />}</Field>
            <Field label="Peran">{(id) => <Input id={id} value={persona.role} maxLength={80} onChange={(e) => p({ role: e.target.value })} />}</Field>
          </FormGrid>
          <Field label="Gaya bicara">
            {(id) => (
              <Select id={id} value={persona.tone} onChange={(e) => p({ tone: e.target.value as Persona["tone"] })}>
                <option value="hangat">Hangat — seperti pemilik yang senang ditanya</option>
                <option value="profesional">Profesional — jelas dan meyakinkan</option>
                <option value="santai">Santai — akrab, tetap sopan</option>
                <option value="formal">Formal — rapi dan resmi</option>
              </Select>
            )}
          </Field>
          <Field label={`Sapaan pembuka (${locale.toUpperCase()})`}>
            {(id) => <Textarea id={id} rows={2} value={persona.greeting[locale]} maxLength={400} onChange={(e) => p({ greeting: { ...persona.greeting, [locale]: e.target.value } })} />}
          </Field>
          <Field label={`Pertanyaan contoh (${locale.toUpperCase()})`} hint="Maksimal 6. Diambil dari pertanyaan terpopuler bekerja paling baik.">
            {(id) => <ChipsInput id={id} value={persona.suggestions[locale]} max={6} onChange={(list) => p({ suggestions: { ...persona.suggestions, [locale]: list } })} placeholder="ketik lalu Enter" />}
          </Field>
          <Field label="Catatan gaya" hint="Mis. “Panggil pengunjung ‘Kak’. Satu emoji per jawaban boleh.”">
            {(id, describedBy) => <Textarea id={id} rows={2} value={persona.styleNotes} maxLength={600} onChange={(e) => p({ styleNotes: e.target.value })} aria-describedby={describedBy} />}
          </Field>
          <Field label="Topik terlarang" hint="Bot menolak dan mengalihkan ke tim. Mis. diagnosa medis, nego harga grosir.">
            {(id) => <ChipsInput id={id} value={persona.forbiddenTopics} onChange={(list) => p({ forbiddenTopics: list })} placeholder="ketik lalu Enter" />}
          </Field>

          <div className={styles.divider} />
          <h3 className={styles.sectionTitle}>Avatar</h3>
          <Choices
            label="Avatar asisten"
            value={theme.avatarMode}
            onChange={(avatarMode) => t({ avatarMode })}
            options={[
              { value: "photo", label: "Foto", hint: "Logo atau foto yang Anda unggah" },
              { value: "initial", label: "Inisial", hint: `Huruf “${persona.name.trim()[0]?.toUpperCase() ?? "A"}” di lingkaran` },
              { value: "none", label: "Tanpa avatar", hint: "Lingkaran dihilangkan sepenuhnya" },
            ]}
          />
          {theme.avatarMode === "photo" && <AvatarUpload project={project} />}

          <div className={styles.divider} />
          <h3 className={styles.sectionTitle}>Saat widget tertutup</h3>
          <Choices
            label="Bentuk tombol"
            value={theme.launcherStyle}
            onChange={(launcherStyle) => t({ launcherStyle })}
            options={[
              { value: "pill", label: "Pil berlabel", hint: "Avatar + kalimat ajakan" },
              { value: "bubble", label: "Bulat", hint: "Ringkas, satu ikon" },
              { value: "square", label: "Kotak membulat", hint: "Ikut sudut widget" },
              { value: "teaser", label: "Bulat + sapaan", hint: "Gelembung ajakan muncul sendiri" },
            ]}
          />
          <FormGrid>
            <Field label={`Kalimat ajakan (${locale.toUpperCase()})`} hint={theme.launcherStyle === "bubble" || theme.launcherStyle === "square" ? "Dibacakan pembaca layar; tidak tampil di tombol bulat/kotak." : undefined}>
              {(id, describedBy) => (
                <Input id={id} value={theme.launcherLabel[locale]} maxLength={60} aria-describedby={describedBy} onChange={(e) => t({ launcherLabel: { ...theme.launcherLabel, [locale]: e.target.value } })} />
              )}
            </Field>
            <Field label="Ikon tombol" hint={theme.launcherStyle !== "pill" && theme.avatarMode !== "none" ? "Dipakai bila tidak ada foto/inisial." : undefined}>
              {(id, describedBy) => (
                <Select id={id} value={theme.launcherIcon} aria-describedby={describedBy} onChange={(e) => t({ launcherIcon: e.target.value as WidgetTheme["launcherIcon"] })}>
                  <option value="chat">Gelembung chat</option>
                  <option value="sparkle">Kilau AI</option>
                  <option value="help">Tanda tanya</option>
                  <option value="wave">Lambaian tangan</option>
                </Select>
              )}
            </Field>
          </FormGrid>
          <Field label="Pemancing perhatian" hint="Gerakan halus pada tombol sampai pengunjung pertama kali membuka chat. Mati otomatis bila perangkat meminta gerak minimal.">
            {(id, describedBy) => (
              <Select id={id} value={theme.attention} aria-describedby={describedBy} onChange={(e) => t({ attention: e.target.value as WidgetTheme["attention"] })}>
                <option value="none">Tidak ada</option>
                <option value="pulse">Denyut — cincin cahaya</option>
                <option value="bounce">Pantul — melompat kecil</option>
                <option value="wiggle">Goyang — melambai</option>
              </Select>
            )}
          </Field>

          <div className={styles.divider} />
          <h3 className={styles.sectionTitle}>Panel chat</h3>
          <FormGrid columns={3}>
            <Field label="Warna utama">
              {(id) => (
                <div className={styles.color}>
                  <input id={id} type="color" value={theme.accent} onChange={(e) => t({ accent: e.target.value.toUpperCase() })} />
                  <Input value={theme.accent} onChange={(e) => /^#[0-9a-fA-F]{6}$/.test(e.target.value) && t({ accent: e.target.value })} aria-label="Kode warna utama" />
                </div>
              )}
            </Field>
            <Field label="Warna teks di atasnya">
              {(id) => (
                <div className={styles.color}>
                  <input id={id} type="color" value={theme.accentContrast} onChange={(e) => t({ accentContrast: e.target.value.toUpperCase() })} />
                  <Input value={theme.accentContrast} onChange={(e) => /^#[0-9a-fA-F]{6}$/.test(e.target.value) && t({ accentContrast: e.target.value })} aria-label="Kode warna teks" />
                </div>
              )}
            </Field>
            <Field label={`Sudut (${theme.radius}px)`}>{(id) => <input id={id} type="range" min={0} max={28} value={theme.radius} onChange={(e) => t({ radius: Number(e.target.value) })} className={styles.range} />}</Field>
          </FormGrid>
          <FormGrid columns={3}>
            <Field label="Posisi">
              {(id) => (
                <Select id={id} value={theme.position} onChange={(e) => t({ position: e.target.value as WidgetTheme["position"] })}>
                  <option value="right">Kanan bawah</option>
                  <option value="left">Kiri bawah</option>
                </Select>
              )}
            </Field>
            <Field label="Permukaan">
              {(id) => (
                <Select id={id} value={theme.surface} onChange={(e) => t({ surface: e.target.value as WidgetTheme["surface"] })}>
                  <option value="light">Terang</option>
                  <option value="dark">Gelap</option>
                  <option value="auto">Ikuti perangkat</option>
                </Select>
              )}
            </Field>
            <Field label="Font" hint="Kosong = ikut website.">
              {(id, describedBy) => <Input id={id} value={theme.fontFamily} onChange={(e) => t({ fontFamily: e.target.value })} aria-describedby={describedBy} />}
            </Field>
          </FormGrid>
          <Field label="Animasi buka-tutup" hint="Di ponsel panel selalu naik dari bawah layar.">
            {(id, describedBy) => (
              <Select id={id} value={theme.animation} aria-describedby={describedBy} onChange={(e) => t({ animation: e.target.value as WidgetTheme["animation"] })}>
                <option value="pop">Pop — membesar dari tombol</option>
                <option value="slide">Slide — naik dari bawah</option>
                <option value="fade">Fade — muncul perlahan</option>
                <option value="spring">Spring — memantul hidup</option>
              </Select>
            )}
          </Field>

          <div className={styles.divider} />
          <h3 className={styles.sectionTitle}>Bahasa</h3>
          <Switch
            checked={theme.languageSwitch}
            onChange={(languageSwitch) => t({ languageSwitch })}
            label="Multi bahasa (ID/EN)"
            description="Tombol ID/EN tampil di kepala widget dan pengunjung bisa berganti bahasa kapan saja. Cocok untuk website dua bahasa; website juga bisa memanggil NavibizChat.setLocale(“en”) dari tombol bahasanya sendiri."
          />
          {englishMissing && <p className={styles.warning}>Lengkapi sapaan pembuka dan kalimat ajakan versi EN (ganti “Bahasa pratinjau” ke EN) agar pengunjung berbahasa Inggris tidak melihat teks kosong.</p>}
        </div>
        <div className={styles.preview}>
          <WidgetPreview persona={persona} theme={theme} locale={locale} projectName={project.name} onLocaleChange={setLocale} />
        </div>
      </div>
      {save.isError && <p className={styles.error}>{save.error.message}</p>}
    </Card>
  );
}

/** Large radio cards: for choices whose difference is easier to read than to name. */
function Choices<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: { value: T; label: string; hint: string }[]; onChange: (value: T) => void }) {
  return (
    <div className={styles.choices} role="radiogroup" aria-label={label}>
      {options.map((option) => (
        <button key={option.value} type="button" role="radio" aria-checked={option.value === value} className={styles.choice} onClick={() => onChange(option.value)}>
          <strong>{option.label}</strong>
          <span>{option.hint}</span>
        </button>
      ))}
    </div>
  );
}

const AVATAR_SIZE = 320;

/**
 * Crops to a centred square and scales to 320 px, so a 4 MB phone photo
 * uploads as a few dozen kilobytes. WebP where the browser can encode it
 * (Safari cannot, and falls back to PNG).
 */
async function squareAvatar(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const side = Math.min(bitmap.width, bitmap.height);
  const size = Math.min(AVATAR_SIZE, side);
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  canvas.getContext("2d")!.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, size, size);
  bitmap.close();
  const encode = (type: string) => new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, 0.9));
  const webp = await encode("image/webp");
  return webp?.type === "image/webp" ? webp : (await encode("image/png"))!;
}

/** One avatar per project: upload, replace, or remove. Saved immediately, apart from the form. */
function AvatarUpload({ project }: { project: Project }) {
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const invalidate = [keys.project(project.id)];
  const upload = useAction(async (file: File) => {
    if (!/^image\/(png|jpeg|webp)$/.test(file.type)) throw new Error("Format foto harus PNG, JPG, atau WebP.");
    await api.upload(`/projects/${project.id}/avatar`, await squareAvatar(file));
  }, invalidate);
  const remove = useAction(() => api.delete(`/projects/${project.id}/avatar`), invalidate);
  const url = project.theme.avatarUrl;
  const pick = () => input.current?.click();

  return (
    <div className={styles.avatarRow}>
      <button type="button" className={styles.avatarDrop} onClick={pick} aria-label={url ? "Ganti foto avatar" : "Unggah foto avatar"}>
        {url ? <img src={url} alt="" /> : <ImagePlus aria-hidden />}
      </button>
      <div className={styles.avatarText}>
        <strong>{url ? "Foto avatar" : "Belum ada foto"}</strong>
        <span>PNG, JPG, atau WebP. Dipotong persegi dan diperkecil otomatis. Satu foto per proyek.</span>
        <div className={styles.avatarActions}>
          <Button size="sm" icon={<Upload aria-hidden />} loading={upload.isPending} onClick={pick}>
            {url ? "Ganti" : "Unggah foto"}
          </Button>
          {url && (
            <Button
              size="sm"
              variant="ghost"
              icon={<Trash2 aria-hidden />}
              loading={remove.isPending}
              onClick={async () => {
                await remove.mutateAsync(undefined);
                toast("Foto avatar dihapus.");
              }}
            >
              Hapus
            </Button>
          )}
        </div>
        {(upload.error ?? remove.error) && <p className={styles.error}>{(upload.error ?? remove.error)!.message}</p>}
      </div>
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={async (event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (!file) return;
          await upload.mutateAsync(file);
          toast("Foto avatar disimpan.");
        }}
      />
    </div>
  );
}

const LIMIT_FIELDS: { key: keyof Limits; label: string; hint: string; min: number; max: number }[] = [
  { key: "messagesPerVisitorPerDay", label: "Pesan per pengunjung per hari", hint: "Melindungi saldo dari spam.", min: 1, max: 500 },
  { key: "messagesPerIpPerDay", label: "Pesan per alamat IP per hari", hint: "Menahan bot yang mengganti identitas.", min: 1, max: 5000 },
  { key: "maxMessageChars", label: "Panjang pesan maksimum (karakter)", hint: "Pesan lebih panjang ditolak.", min: 50, max: 4000 },
  { key: "maxOutputTokens", label: "Panjang jawaban maksimum (token)", hint: "Membatasi biaya per jawaban.", min: 100, max: 2000 },
  { key: "historyTurns", label: "Riwayat yang diingat (giliran)", hint: "Konteks percakapan yang dikirim ulang.", min: 0, max: 20 },
  { key: "requestsPerMinute", label: "Permintaan per menit per kunci", hint: "Batas teknis API (Lampiran AI-3).", min: 1, max: 600 },
];

function LimitsSection({ project }: { project: Project }) {
  const toast = useToast();
  const [limits, setLimits] = useState<Limits>(project.limits);
  const save = useSaveProject(project.id);
  useEffect(() => {
    setLimits(project.limits);
  }, [project.limits]);
  const dirty = JSON.stringify(limits) !== JSON.stringify(project.limits);

  return (
    <Card
      title="Batas & perlindungan saldo"
      description="Lampiran III Pasal 7: batas pesan, panjang jawaban, dan anti-bot agar saldo tidak habis karena spam."
      actions={
        <Button
          variant="primary"
          icon={<Save aria-hidden />}
          disabled={!dirty}
          loading={save.isPending}
          onClick={async () => {
            await save.mutateAsync({ limits });
            toast("Batas disimpan.");
          }}
        >
          Simpan
        </Button>
      }
    >
      <FormGrid columns={3}>
        {LIMIT_FIELDS.map((field) => (
          <Field key={field.key} label={field.label} hint={field.hint}>
            {(id, describedBy) => (
              <Input
                id={id}
                type="number"
                min={field.min}
                max={field.max}
                value={limits[field.key]}
                aria-describedby={describedBy}
                onChange={(e) => setLimits({ ...limits, [field.key]: Number(e.target.value) })}
              />
            )}
          </Field>
        ))}
      </FormGrid>
      {save.isError && <p className={styles.error}>{save.error.message}</p>}
    </Card>
  );
}

function ProjectSection({ project }: { project: Project }) {
  const toast = useToast();
  const billing = useBilling();
  const save = useSaveProject(project.id);

  return (
    <Card title="Data proyek" description="Hanya admin. Status “Aktif” mewajibkan pengetahuan sudah terbit.">
      <form
        className={styles.form}
        onSubmit={async (event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const value = (name: string) => String(form.get(name) ?? "").trim() || null;
          await save.mutateAsync({
            name: value("name"),
            clientName: value("clientName"),
            product: value("product"),
            packageCode: value("packageCode"),
            status: value("status"),
            model: value("model"),
            defaultLocale: value("defaultLocale"),
            websiteUrl: value("websiteUrl"),
            privacyUrl: value("privacyUrl"),
            whatsapp: value("whatsapp"),
            picName: value("picName"),
            picEmail: value("picEmail"),
            picWhatsapp: value("picWhatsapp"),
            turnstileSiteKey: value("turnstileSiteKey"),
            notes: value("notes"),
          });
          toast("Data proyek disimpan.");
        }}
      >
        <FormGrid columns={3}>
          <Field label="Nama usaha">{(id) => <Input id={id} name="name" defaultValue={project.name} required />}</Field>
          <Field label="Nama klien di SPK">{(id) => <Input id={id} name="clientName" defaultValue={project.clientName} required />}</Field>
          <Field label="Produk AI" hint="Versi berakhiran L memakai Linkage. Perubahan produk tercatat di audit log.">
            {(id, describedBy) => (
              <Select id={id} name="product" defaultValue={project.product} aria-describedby={describedBy}>
                {Object.entries(PRODUCT_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Paket website" hint="Hanya untuk Add-on (website buatan Navibiz).">
            {(id, describedBy) => (
              <Select id={id} name="packageCode" defaultValue={project.packageCode ?? ""} aria-describedby={describedBy}>
                <option value="">—</option>
                {PACKAGES.map(([code, label]) => (
                  <option key={code} value={code}>
                    {code} · {label}
                  </option>
                ))}
                {project.packageCode && !PACKAGES.some(([code]) => code === project.packageCode) && <option value={project.packageCode}>{project.packageCode} (v3)</option>}
              </Select>
            )}
          </Field>
          <Field label="Status">
            {(id) => (
              <Select id={id} name="status" defaultValue={project.status}>
                <option value="draft">Draf (belum tayang)</option>
                <option value="active">Aktif</option>
                <option value="paused">Dijeda</option>
              </Select>
            )}
          </Field>
          <Field label="Model AI" hint="Harga per model diatur di Pengaturan → Tarif.">
            {(id, describedBy) => (
              <Select id={id} name="model" defaultValue={project.model} aria-describedby={describedBy}>
                {(billing.data?.prices ?? [{ model: project.model, label: project.model }]).map((price) => (
                  <option key={price.model} value={price.model}>
                    {price.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Bahasa bawaan">
            {(id) => (
              <Select id={id} name="defaultLocale" defaultValue={project.defaultLocale}>
                <option value="id">Indonesia</option>
                <option value="en">English</option>
              </Select>
            )}
          </Field>
          <Field label="WhatsApp usaha">{(id) => <Input id={id} name="whatsapp" defaultValue={project.whatsapp ?? ""} />}</Field>
          <Field label="Website">{(id) => <Input id={id} name="websiteUrl" type="url" defaultValue={project.websiteUrl ?? ""} />}</Field>
          <Field label="Kebijakan privasi">{(id) => <Input id={id} name="privacyUrl" type="url" defaultValue={project.privacyUrl ?? ""} />}</Field>
          <Field label="Nama PIC klien">{(id) => <Input id={id} name="picName" defaultValue={project.pic.name ?? ""} />}</Field>
          <Field label="Email PIC" hint="Tujuan pengingat saldo.">
            {(id, describedBy) => <Input id={id} name="picEmail" type="email" defaultValue={project.pic.email ?? ""} aria-describedby={describedBy} />}
          </Field>
          <Field label="WhatsApp PIC" hint="Tujuan pengingat saldo dan notifikasi formulir kontak website.">
            {(id, describedBy) => <Input id={id} name="picWhatsapp" defaultValue={project.pic.whatsapp ?? ""} aria-describedby={describedBy} />}
          </Field>
          <Field label="Turnstile site key" hint="Opsional. Verifikasi anti-robot pada pesan pertama widget; butuh secret di gateway.">
            {(id, describedBy) => <Input id={id} name="turnstileSiteKey" defaultValue={project.turnstileSiteKey ?? ""} placeholder="0x4AAAAAAA…" aria-describedby={describedBy} />}
          </Field>
        </FormGrid>
        <Field label="Catatan internal">{(id) => <Textarea id={id} name="notes" rows={3} defaultValue={project.notes ?? ""} />}</Field>
        <div>
          <Button type="submit" variant="primary" icon={<Save aria-hidden />} loading={save.isPending}>
            Simpan data proyek
          </Button>
        </div>
        {save.isError && <p className={styles.error}>{save.error.message}</p>}
      </form>
    </Card>
  );
}

/**
 * Archive, restore, and permanent deletion. Archiving is the safe, reversible
 * step: the bot stops answering and the project leaves the working lists.
 * Deleting is only offered from the archive, behind typing the slug.
 */
function ArchiveSection({ project }: { project: Project }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const invalidate = [keys.project(project.id), ["overview"]];
  const archive = useAction(() => api.post(`/projects/${project.id}/archive`), invalidate);
  const restore = useAction(() => api.post(`/projects/${project.id}/restore`), invalidate);
  const remove = useAction(() => api.deleteWith(`/projects/${project.id}`, { confirm: typed }), [["overview"]]);
  const error = archive.error ?? restore.error ?? remove.error;

  if (!project.archivedAt) {
    return (
      <Card title="Arsipkan proyek" description="Chatbot berhenti menjawab dan proyek pindah ke bagian Arsip di sidebar. Semua data tetap utuh dan bisa diaktifkan lagi kapan saja.">
        <Button
          icon={<Archive aria-hidden />}
          loading={archive.isPending}
          onClick={async () => {
            await archive.mutateAsync(undefined);
            toast(`${project.name} diarsipkan.`);
          }}
        >
          Arsipkan proyek
        </Button>
        {error && <p className={styles.error}>{error.message}</p>}
      </Card>
    );
  }

  return (
    <Card title="Proyek diarsipkan" description="Aktifkan lagi untuk mengembalikannya dengan status semula, atau hapus permanen beserta seluruh datanya.">
      <div className={styles.archiveActions}>
        <Button
          variant="primary"
          icon={<ArchiveRestore aria-hidden />}
          loading={restore.isPending}
          onClick={async () => {
            await restore.mutateAsync(undefined);
            toast(`${project.name} aktif kembali dari arsip.`);
          }}
        >
          Aktifkan lagi
        </Button>
        {!confirming && (
          <Button variant="danger" icon={<Trash2 aria-hidden />} onClick={() => setConfirming(true)}>
            Hapus permanen…
          </Button>
        )}
      </div>
      {confirming && (
        <div className={styles.danger}>
          <p>
            Pengetahuan, rujukan, percakapan, riwayat saldo, kunci API, akun klien, dan foto avatar proyek ini akan dihapus dan <strong>tidak bisa dipulihkan</strong>.
            {project.balanceMilli > 0 && " Proyek ini masih punya saldo: selesaikan pengembaliannya dulu."}
          </p>
          <Field label={`Ketik ${project.slug} untuk mengonfirmasi`}>{(id) => <Input id={id} value={typed} autoComplete="off" onChange={(e) => setTyped(e.target.value)} />}</Field>
          <div className={styles.archiveActions}>
            <Button
              variant="danger"
              icon={<Trash2 aria-hidden />}
              disabled={typed !== project.slug}
              loading={remove.isPending}
              onClick={async () => {
                await remove.mutateAsync(undefined);
                toast(`${project.name} dihapus permanen.`);
                navigate("/", { replace: true });
              }}
            >
              Hapus permanen
            </Button>
            <Button variant="ghost" onClick={() => (setConfirming(false), setTyped(""))}>
              Batal
            </Button>
          </div>
        </div>
      )}
      {error && <p className={styles.error}>{error.message}</p>}
    </Card>
  );
}
